import React, { useState, useEffect, useCallback } from 'react'
import { apiService } from '@/services/api'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface Employee {
  id: string
  _id?: string
  name: string
  email: string
  phone?: string
  role: 'admin' | 'manager' | 'cashier' | 'staff'
  isActive?: boolean
  createdAt?: string
}

interface EmployeeFormData {
  name: string
  email: string
  phone: string
  role: 'admin' | 'manager' | 'cashier' | 'staff'
  password?: string
  isActive: boolean
}

export const EmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')
  const [submitting, setSubmitting] = useState(false)
  const [pinEmployee, setPinEmployee] = useState<Employee | null>(null)
  const [pinValue, setPinValue] = useState('')
  const [pinSubmitting, setPinSubmitting] = useState(false)

  const { showSuccess, showError } = useNotificationActions()

  const roles = ['All', 'admin', 'manager', 'cashier', 'staff']
  const statuses = ['All', 'Active', 'Inactive']

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiService.get<{ success: boolean; data: Employee[] }>('/employees')
      if (response.success && response.data) {
        setEmployees(response.data)
      }
    } catch (error: any) {
      showError('Failed to load employees', error?.response?.data?.message || 'Could not fetch employee data')
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // showError is stable now due to useCallback memoization

  useEffect(() => {
    fetchEmployees()
  }, [fetchEmployees])

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.role?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = filterRole === 'All' || employee.role === filterRole
    const isActive = employee.isActive !== false
    const matchesStatus = filterStatus === 'All' || 
      (filterStatus === 'Active' && isActive) || 
      (filterStatus === 'Inactive' && !isActive)
    return matchesSearch && matchesRole && matchesStatus
  })

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'text-purple-400 bg-purple-500/20 border border-purple-500/30'
      case 'manager': return 'text-blue-400 bg-blue-500/20 border border-blue-500/30'
      case 'cashier': return 'text-green-400 bg-green-500/20 border border-green-500/30'
      case 'staff': return 'text-yellow-400 bg-yellow-500/20 border border-yellow-500/30'
      default: return 'text-muted-foreground bg-muted/20 border border-border'
    }
  }

  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? 'text-success bg-success/20 border border-success/30'
      : 'text-muted-foreground bg-muted/20 border border-border'
  }

  const addEmployee = async (formData: EmployeeFormData) => {
    if (!formData.password) {
      showError('Password required', 'Please enter a password for the new employee')
      return
    }

    setSubmitting(true)
    try {
      const response = await apiService.post<{ success: boolean; data: Employee; message?: string }>('/employees', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        password: formData.password,
        isActive: formData.isActive
      })

      if (response.success) {
        showSuccess('Employee Added', response.message || 'New employee has been created')
        setShowAddForm(false)
        fetchEmployees()
      }
    } catch (error: any) {
      showError('Failed to add employee', error?.response?.data?.message || 'Could not create employee')
    } finally {
      setSubmitting(false)
    }
  }

  const updateEmployee = async (id: string, formData: Partial<EmployeeFormData>) => {
    setSubmitting(true)
    try {
      const response = await apiService.put<{ success: boolean; data: Employee; message?: string }>(`/employees/${id}`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        isActive: formData.isActive
      })

      if (response.success) {
        showSuccess('Employee Updated', response.message || 'Employee details have been updated')
        setEditingEmployee(null)
        fetchEmployees()
      }
    } catch (error: any) {
      showError('Failed to update employee', error?.response?.data?.message || 'Could not update employee')
    } finally {
      setSubmitting(false)
    }
  }

  const setEmployeePin = async (id: string, pin: string | null) => {
    setPinSubmitting(true)
    try {
      await apiService.patch<{ success: boolean; message?: string }>(`/employees/${id}/pin`, { pin })
      showSuccess('PIN Updated', pin ? 'Quick login PIN has been set.' : 'PIN has been cleared.')
      setPinEmployee(null)
      setPinValue('')
    } catch (error: any) {
      showError('PIN failed', error?.response?.data?.message || 'Could not update PIN')
    } finally {
      setPinSubmitting(false)
    }
  }

  const deleteEmployee = async (id: string) => {
    if (!confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      return
    }

    try {
      const response = await apiService.delete<{ success: boolean; message?: string }>(`/employees/${id}`)
      if (response.success) {
        showSuccess('Employee Deleted', response.message || 'Employee has been removed')
        fetchEmployees()
      }
    } catch (error: any) {
      showError('Failed to delete employee', error?.response?.data?.message || 'Could not delete employee')
    }
  }

  const activeCount = employees.filter(e => e.isActive !== false).length
  const inactiveCount = employees.filter(e => e.isActive === false).length

  return (
    <div className="w-full py-6">
      <div className="py-6">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Employee Management</h1>
            <p className="text-muted-foreground mt-2">Manage your team and employee accounts</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
          >
            Add Employee
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="glass-card">
            <h3 className="text-muted-foreground text-sm font-medium">Total Employees</h3>
            <p className="text-3xl font-bold text-foreground">{employees.length}</p>
          </div>
          <div className="glass-card">
            <h3 className="text-muted-foreground text-sm font-medium">Active</h3>
            <p className="text-3xl font-bold text-success">{activeCount}</p>
          </div>
          <div className="glass-card">
            <h3 className="text-muted-foreground text-sm font-medium">Inactive</h3>
            <p className="text-3xl font-bold text-muted-foreground">{inactiveCount}</p>
          </div>
          <div className="glass-card">
            <h3 className="text-muted-foreground text-sm font-medium">Admins</h3>
            <p className="text-3xl font-bold text-purple-400">
              {employees.filter(e => e.role === 'admin').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {roles.map(role => (
                <option key={role} value={role}>{role === 'All' ? 'All Roles' : role}</option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {statuses.map(status => (
                <option key={status} value={status}>{status === 'All' ? 'All Statuses' : status}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="glass-card">
            <LoadingSpinner 
              size="md"
              message="Loading Team"
              submessage="Fetching employee data..."
            />
          </div>
        ) : (
          /* Employees Table — desktop */
          <>
            <div className="glass-card overflow-hidden hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/20">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredEmployees.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">
                          {searchTerm || filterRole !== 'All' || filterStatus !== 'All'
                            ? 'No employees match your filters'
                            : 'No employees found. Add your first employee!'}
                        </td>
                      </tr>
                    ) : (
                      filteredEmployees.map(employee => (
                        <tr key={employee.id || employee._id} className="hover:bg-muted/10">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                                {employee.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-foreground">{employee.name}</div>
                                <div className="text-sm text-muted-foreground">{employee.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(employee.role)}`}>
                              {employee.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {employee.phone || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {employee.createdAt ? new Date(employee.createdAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(employee.isActive !== false)}`}>
                              {employee.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => setEditingEmployee(employee)}
                                className="text-primary hover:text-primary/80"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => { setPinEmployee(employee); setPinValue(''); }}
                                className="text-muted-foreground hover:text-foreground"
                                title="Set quick login PIN"
                              >
                                Set PIN
                              </button>
                              <button
                                onClick={() => deleteEmployee(employee.id || employee._id || '')}
                                className="text-error hover:text-error/80"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Employees Card List — mobile */}
            <div className="md:hidden space-y-3">
              {filteredEmployees.length === 0 ? (
                <div className="glass-card py-10 text-center text-muted-foreground">
                  {searchTerm || filterRole !== 'All' || filterStatus !== 'All'
                    ? 'No employees match your filters'
                    : 'No employees found. Add your first employee!'}
                </div>
              ) : (
                filteredEmployees.map(employee => (
                  <div key={employee.id || employee._id} className="glass-card">
                    {/* Top row: avatar + name/email + status badge */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 shrink-0 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                          {employee.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{employee.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{employee.email}</p>
                        </div>
                      </div>
                      <span className={`shrink-0 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(employee.isActive !== false)}`}>
                        {employee.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Middle row: role + phone */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(employee.role)}`}>
                        {employee.role}
                      </span>
                      {employee.phone && (
                        <span className="text-xs text-muted-foreground">{employee.phone}</span>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 pt-3 border-t border-border">
                      <button
                        onClick={() => setEditingEmployee(employee)}
                        className="flex-1 py-1.5 text-xs font-medium rounded-lg border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => { setPinEmployee(employee); setPinValue(''); }}
                        className="flex-1 py-1.5 text-xs font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted/20 transition-colors"
                      >
                        Set PIN
                      </button>
                      <button
                        onClick={() => deleteEmployee(employee.id || employee._id || '')}
                        className="flex-1 py-1.5 text-xs font-medium rounded-lg border border-error/40 text-error hover:bg-error/10 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Set PIN Modal */}
        {pinEmployee && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">Quick login PIN</h2>
                <button onClick={() => { setPinEmployee(null); setPinValue(''); }} className="p-2 hover:bg-muted rounded-lg">✕</button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {pinEmployee.name} — 4–6 digit PIN for fast sign-in at the register.
              </p>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pinValue}
                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 4–6 digits"
                className="w-full px-4 py-2.5 bg-background border border-border rounded-lg mb-4"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pinSubmitting || pinValue.length < 4}
                  onClick={() => setEmployeePin(pinEmployee.id || pinEmployee._id || '', pinValue)}
                  className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
                >
                  {pinSubmitting ? 'Saving...' : 'Set PIN'}
                </button>
                <button
                  type="button"
                  disabled={pinSubmitting}
                  onClick={() => setEmployeePin(pinEmployee.id || pinEmployee._id || '', null)}
                  className="py-2 px-4 bg-muted rounded-lg font-medium disabled:opacity-50"
                >
                  Clear PIN
                </button>
                <button
                  type="button"
                  onClick={() => { setPinEmployee(null); setPinValue(''); }}
                  className="py-2 px-4 rounded-lg border border-border hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Employee Modal */}
        {(showAddForm || editingEmployee) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-foreground">
                    {editingEmployee ? 'Edit Employee' : 'Create Employee Account'}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {editingEmployee 
                      ? 'Update employee information' 
                      : 'Create a new login account for an employee'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAddForm(false)
                    setEditingEmployee(null)
                  }}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.target as HTMLFormElement)
                const selectedRole = formData.get('role') as 'admin' | 'manager' | 'cashier' | 'staff'
                
                // Warn when creating admin account
                if (!editingEmployee && selectedRole === 'admin') {
                  if (!confirm('You are about to create an ADMIN account. Admins have full access to all system features including employee management, settings, and sensitive data. Continue?')) {
                    return
                  }
                }
                
                const employeeData: EmployeeFormData = {
                  name: formData.get('name') as string,
                  email: formData.get('email') as string,
                  phone: formData.get('phone') as string,
                  role: selectedRole,
                  password: formData.get('password') as string || undefined,
                  isActive: formData.get('isActive') === 'true'
                }

                if (editingEmployee) {
                  updateEmployee(editingEmployee.id || editingEmployee._id || '', employeeData)
                } else {
                  addEmployee(employeeData)
                }
              }}>
                <div className="space-y-5">
                  {/* Account Info Section */}
                  {!editingEmployee && (
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">🔐</span>
                        <div>
                          <h3 className="font-semibold text-foreground text-sm">Login Credentials</h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            This will create a new account. The employee can use these credentials to log into the system.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                    <input
                      name="name"
                      placeholder="Enter employee's full name"
                      defaultValue={editingEmployee?.name || ''}
                      required
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Email Address * 
                      {!editingEmployee && <span className="text-xs text-muted-foreground font-normal ml-1">(used for login)</span>}
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="employee@example.com"
                      defaultValue={editingEmployee?.email || ''}
                      required
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  {/* Password - only for new employees */}
                  {!editingEmployee && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Password * <span className="text-xs text-muted-foreground font-normal">(min 6 characters)</span>
                      </label>
                      <input
                        name="password"
                        type="password"
                        placeholder="Create a secure password"
                        required
                        minLength={6}
                        className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <p className="text-xs text-muted-foreground mt-1.5">
                        💡 Share this password securely with the employee. They can change it later in their profile.
                      </p>
                    </div>
                  )}

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Phone Number</label>
                    <input
                      name="phone"
                      placeholder="Enter phone number (optional)"
                      defaultValue={editingEmployee?.phone || ''}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  {/* Role Selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Role & Permissions *</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: 'staff', label: 'Staff', icon: '👤', desc: 'POS access only', color: 'border-green-500/30 bg-green-500/5' },
                        { value: 'cashier', label: 'Cashier', icon: '💵', desc: 'POS + Customers', color: 'border-blue-500/30 bg-blue-500/5' },
                        { value: 'manager', label: 'Manager', icon: '📊', desc: 'Most features', color: 'border-purple-500/30 bg-purple-500/5' },
                        { value: 'admin', label: 'Admin', icon: '👑', desc: 'Full access', color: 'border-red-500/30 bg-red-500/5' },
                      ].map((role) => (
                        <label
                          key={role.value}
                          className={`relative flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all hover:border-primary/50 ${role.color}`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={role.value}
                            defaultChecked={editingEmployee?.role === role.value || (!editingEmployee && role.value === 'cashier')}
                            className="sr-only peer"
                          />
                          <div className="flex items-center gap-3 w-full peer-checked:text-primary">
                            <span className="text-xl">{role.icon}</span>
                            <div>
                              <p className="font-semibold text-sm">{role.label}</p>
                              <p className="text-xs text-muted-foreground">{role.desc}</p>
                            </div>
                          </div>
                          <div className="absolute top-2 right-2 w-4 h-4 rounded-full border-2 border-border peer-checked:border-primary peer-checked:bg-primary flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-white opacity-0 peer-checked:opacity-100"></div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Account Status</label>
                    <select
                      name="isActive"
                      defaultValue={editingEmployee?.isActive !== false ? 'true' : 'false'}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="true">✅ Active - Can log in and use the system</option>
                      <option value="false">⛔ Inactive - Account disabled</option>
                    </select>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-8 pt-6 border-t border-border">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false)
                      setEditingEmployee(null)
                    }}
                    disabled={submitting}
                    className="flex-1 py-3 bg-muted text-foreground font-semibold rounded-xl hover:bg-muted/80 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Saving...
                      </>
                    ) : (
                      <>
                        {editingEmployee ? '💾 Update Employee' : '➕ Create Account'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
